/**
 * Générateur — Découvrir la notion de puissance d'un nombre et sa notation
 * dans le cas du carré et du cube.
 * Attendu ID : 926 | Niveau : 5ème | Domaine : Puissances (cluster 1, O28)
 *
 * Plan de cartes (validé 2026-07-11) :
 *
 *   Carte « bon calcul » — Ex.13 Choisir le bon calcul
 *     qcm_unique (deux directions : notation → développement, développement →
 *     notation) + relier (mélange puissances et produit simple 2 × c → c + c,
 *     comme le doc). Piège central : aⁿ = a × n. Exclusion du couple a = 2 /
 *     n = 2 (2 × 2 = 2 + 2 = 4 rendrait le piège « somme » défendable) ;
 *     en relier, paires a²/a³ de même base (mauvais compte de facteurs),
 *     bases toutes distinctes → sources et cibles distinctes par construction.
 *
 *   Carte « V/F » — Ex.11 Vrai ou faux ?
 *     tableau_vf : 5 lignes « aⁿ = v » à notations toutes distinctes ;
 *     vraies par construction (v = aⁿ), fausses par familles (v = a × n —
 *     exclu si égal à aⁿ ; échange carré/cube). 2-3 vraies, jamais tout-V/F.
 *
 *   Carte « aires » — Ex.14 Avec des aires (figure des quatre carrés)
 *     Figure du doc non extraite → RECONSTRUITE en SVG (grand carré découpé
 *     en 4 petits carrés, côté coté). texte_trou_drag (aire petit carré,
 *     aire figure, unité cm² à glisser parmi cm/cm²/cm³ — le « menu
 *     déroulant » du doc) + qcm_unique (sens direct côté → aire, sens
 *     inverse aire totale (2c)² → côté, réponses décimales x,5 conformes au
 *     cadrage « décimal positif », ex. 81 → 4,5). Arithmétique en
 *     demi-unités entières (zéro flottant).
 *
 *   Carte « volume » — Ex.18 Le Rubik's Cube (image → SVG cube n×n×n)
 *     texte_trou_saisie (arête lue sur la figure, développement, résultat)
 *     + qcm_unique (n³ contre n², 3n, 6n², n²+n, 4n² — familles dédupliquées
 *     numériquement : 3n = n² pour n = 3, 6n² = 2n³ pour n = 3). Double
 *     lecture du doc préservée (volume en cm³ OU compte des petits cubes).
 *     Arête 10 exclue (anti-doublon : le cube de 10 est porté par gen_928).
 *
 *   Carte « décimaux » — Créé n°17 (comble le « a² et a³ pour un décimal
 *     positif » du cadrage) : texte_trou_saisie (un décimal par trou) +
 *     qcm_unique. d en dixièmes entiers (d ≤ 2,5 ; cube réservé à d ≤ 0,5) ;
 *     distracteurs du doc : virgule décalée (0,5² → 2,5), d × n (1,2² → 2,4),
 *     cube→carré (0,2³ → 0,04). Calculs en dixièmes/centièmes/millièmes
 *     ENTIERS, formatage français sans zéros finaux.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, filtrerDistracteurs,
    basePayload,
} from '../../../utils';

const ATTENDU_ID = 926;

const L_BONCALCUL = 'Choisir le calcul qui correspond';
const L_VF = 'Vrai ou faux : carrés et cubes';
const L_AIRES = 'Aires et carrés';
const L_VOLUME = 'Volume du cube';
const L_DECIMAUX = 'Carrés et cubes de décimaux';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Notation puissance en Unicode (rend partout : choix QCM, lignes V/F, drags)
const SUP = { 2: '²', 3: '³' };
const puiss = (a, n) => `${a}${SUP[n]}`;

// développement multiplicatif : devProduit(4, 3) → "4 × 4 × 4"
const devProduit = (a, n) => Array(n).fill(a).join(' × ');

/**
 * Écriture décimale française d'un entier scaled : frFrac(144, 100) → "1,44",
 * frFrac(8, 1000) → "0,008", frFrac(200, 100) → "2". Jamais de zéro final.
 */
const frFrac = (num, den) => {
    const ent = Math.floor(num / den);
    let reste = num % den;
    if (reste === 0) return String(ent);
    let d = den;
    while (reste % 10 === 0) { reste /= 10; d /= 10; }
    const nbDec = String(d).length - 1;
    return `${ent},${String(reste).padStart(nbDec, '0')}`;
};

// dixièmes entiers → décimal français : frT(12) → "1,2", frT(10) → "1"
const frT = (t) => frFrac(t, 10);

// ═════════ SVG — figure des quatre carrés (Ex.14) ═════════

const svgQuatreCarres = (labelCote) => {
    const s = 70;               // côté d'un petit carré (px)
    const x0 = 45, y0 = 34;
    const parts = [
        `<rect x='${x0}' y='${y0}' width='${2 * s}' height='${2 * s}' fill='#E0E7FF' stroke='#1E3A8A' stroke-width='2'/>`,
        `<line x1='${x0 + s}' y1='${y0}' x2='${x0 + s}' y2='${y0 + 2 * s}' stroke='#1E3A8A' stroke-width='2'/>`,
        `<line x1='${x0}' y1='${y0 + s}' x2='${x0 + 2 * s}' y2='${y0 + s}' stroke='#1E3A8A' stroke-width='2'/>`,
    ];
    if (labelCote) {
        // cote du côté d'UN petit carré (accolade simple au-dessus du carré haut-gauche)
        parts.push(
            `<line x1='${x0}' y1='${y0 - 12}' x2='${x0 + s}' y2='${y0 - 12}' stroke='#1E3A8A' stroke-width='1.5'/>`,
            `<line x1='${x0}' y1='${y0 - 17}' x2='${x0}' y2='${y0 - 7}' stroke='#1E3A8A' stroke-width='1.5'/>`,
            `<line x1='${x0 + s}' y1='${y0 - 17}' x2='${x0 + s}' y2='${y0 - 7}' stroke='#1E3A8A' stroke-width='1.5'/>`,
            `<text x='${x0 + s / 2}' y='${y0 - 20}' text-anchor='middle' font-size='15' fill='#1E3A8A' font-family='sans-serif'>${labelCote} cm</text>`,
        );
    }
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 230 ${y0 + 2 * s + 14}'>${parts.join('')}</svg>`;
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

// ═════════ SVG — cube n×n×n en projection cavalière (Ex.18) ═════════

const svgCube = (n, labelArete) => {
    const s = Math.round(120 / n);        // taille d'une cellule de la face avant
    const d = Math.round(s * 0.45);       // décalage de profondeur par unité (→ haut-droite)
    const x0 = 18, y0 = 20 + n * d;
    const H = y0 + n * s + (labelArete ? 26 : 10);
    const W = x0 + n * s + n * d + 14;
    const p = [];
    // face avant (n×n cellules)
    for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
            p.push(`<rect x='${x0 + i * s}' y='${y0 + j * s}' width='${s}' height='${s}' fill='#E0E7FF' stroke='#1E3A8A' stroke-width='1.5'/>`);
        }
    }
    // face du dessus (n colonnes × n profondeurs)
    for (let i = 0; i < n; i++) {
        for (let k = 0; k < n; k++) {
            const ax = x0 + i * s + k * d, ay = y0 - k * d;
            p.push(`<polygon points='${ax},${ay} ${ax + s},${ay} ${ax + s + d},${ay - d} ${ax + d},${ay - d}' fill='#C7D2FE' stroke='#1E3A8A' stroke-width='1.5'/>`);
        }
    }
    // face de droite (n lignes × n profondeurs)
    const x1 = x0 + n * s;
    for (let j = 0; j < n; j++) {
        for (let k = 0; k < n; k++) {
            const ax = x1 + k * d, ay = y0 + j * s - k * d;
            p.push(`<polygon points='${ax},${ay} ${ax + d},${ay - d} ${ax + d},${ay + s - d} ${ax},${ay + s}' fill='#A5B4FC' stroke='#1E3A8A' stroke-width='1.5'/>`);
        }
    }
    if (labelArete) {
        p.push(`<text x='${x0 + (n * s) / 2}' y='${y0 + n * s + 20}' text-anchor='middle' font-size='15' fill='#1E3A8A' font-family='sans-serif'>${labelArete} cm</text>`);
    }
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${W} ${H}'>${p.join('')}</svg>`;
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

// ═════════ Carte 1 — Choisir le bon calcul (Ex.13) ═════════

// (a, n) avec exclusion du couple 2/2 : 2 × 2 = 2 + 2 rendrait deux choix défendables
const tirerBaseExposant = () => {
    const n = randomChoice([2, 3]);
    const a = n === 2 ? randInt(3, 9) : randInt(2, 9);
    return { a, n };
};

const genBonCalculQcm = () => {
    const { a, n } = tirerBaseExposant();
    const direction = randomChoice(['versCalcul', 'versNotation']);
    let texte, correct, candidats;
    if (direction === 'versCalcul') {
        texte = `<p><b>Quel calcul correspond à ${puiss(a, n)} ?</b></p>`;
        correct = devProduit(a, n);
        candidats = [
            `${a} × ${n}`,                            // LA confusion aⁿ = a × n
            Array(n).fill(a).join(' + '),             // confusion produit/somme
            devProduit(a, n === 2 ? 3 : 2),           // mauvais compte de facteurs
            Array(n === 2 ? 3 : 2).fill(a).join(' + '),   // somme ET mauvais compte
        ];
        // a = n = 3 : « 3 × 3 » sort deux fois (familles mult et compte) — le Set
        // dédoublonne, le 4e candidat garantit toujours 3 distracteurs distincts
    } else {
        texte = `<p><b>Comment s'écrit le calcul ${devProduit(a, n)} avec la notation puissance ?</b></p>`;
        correct = puiss(a, n);
        candidats = [
            puiss(a, n === 2 ? 3 : 2),                // échange carré/cube
            `${a} × ${n}`,                            // notation confondue avec le produit
            (a === 2 || a === 3) && a !== n
                ? puiss(n, a)                         // base et exposant renversés (lisible)
                : Array(n).fill(a).join(' + '),       // sinon : somme
        ];
    }
    const distincts = [...new Set(candidats)].filter(c => c !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte, opts(L_BONCALCUL));
    payload._meta.params = { a, n, direction };
    return payload;
};

const genBonCalculRelier = () => {
    // bases toutes distinctes (≥ 3 : évite 2² vs 2 × c) → cibles distinctes par construction
    const [a, b, c] = shuffle([3, 4, 5, 6, 7, 8, 9]).slice(0, 3);
    const m = randomChoice([2, 3]);
    const pairs = shuffle([
        { source: puiss(a, 2), target: devProduit(a, 2) },
        { source: puiss(a, 3), target: devProduit(a, 3) },   // même base : piège du compte de facteurs
        { source: puiss(b, m), target: devProduit(b, m) },
        { source: `2 × ${c}`, target: `${c} + ${c}` },       // le produit simple du doc (2 × 6 → 6 + 6)
    ]);
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque écriture au calcul qui lui correspond.</b></p>',
        opts(L_BONCALCUL));
    payload._meta.params = { a, b, c, m };
    return payload;
};

// ═════════ Carte 2 — Vrai ou faux ? (Ex.11) ═════════

// Pools admissibles par famille (énumérés : la contrainte est structurelle,
// jamais dépendante d'un nombre d'essais).
//   vrai    : v = aⁿ, toujours vrai.
//   mult    : v = a × n — FAUX garanti : (2, 2) exclu (2 × 2 = 2²) ; a × 3 = a³
//             n'a pas de solution entière ≥ 2.
//   echange : v = a^(autre exposant) — FAUX garanti pour a ≥ 2 ; carrés
//             limités à a ≤ 6 pour que le cube affiché reste raisonnable.
const POOLS_VF = { vrai: [], mult: [], echange: [] };
for (let a = 2; a <= 10; a++) {
    POOLS_VF.vrai.push([a, 2]);
    if (a !== 2) POOLS_VF.mult.push([a, 2]);
    if (a <= 6) POOLS_VF.echange.push([a, 2]);
}
for (let a = 2; a <= 6; a++) {
    POOLS_VF.vrai.push([a, 3]);
    POOLS_VF.mult.push([a, 3]);
    POOLS_VF.echange.push([a, 3]);
}

const genVfNotation = () => {
    const lignes = [];
    const bonnes = [];
    const notationsVues = new Set();
    const nbVrai = randInt(2, 3);
    const familles = shuffle([
        ...Array(nbVrai).fill('vrai'),
        ...shuffle(['mult', 'echange', 'mult', 'echange']).slice(0, 5 - nbVrai),
    ]);
    familles.forEach(famille => {
        // premier couple du pool mélangé dont la notation est encore libre —
        // toujours trouvé (pools ≥ 9 éléments, 5 lignes au plus)
        const couple = shuffle(POOLS_VF[famille]).find(([a, n]) => !notationsVues.has(puiss(a, n)));
        if (!couple) return;
        const [a, n] = couple;
        notationsVues.add(puiss(a, n));
        const v = famille === 'vrai' ? a ** n
            : famille === 'mult' ? a * n
                : a ** (n === 2 ? 3 : 2);
        lignes.push(`${puiss(a, n)} = ${v}`);
        bonnes.push(famille === 'vrai' ? 0 : 1);
    });
    const payload = makeVraiFaux(lignes, bonnes,
        '<p><b>Vrai ou faux ?</b> Pour chaque égalité, coche la bonne case.</p>',
        opts(L_VF));
    payload._meta.params = { lignes, bonnes };
    return payload;
};

// ═════════ Carte 3 — Aires et carrés (Ex.14) ═════════

const genAiresDrag = () => {
    const c = randInt(2, 12);
    const petit = c * c;
    const figure = 4 * petit;
    const sens = randomChoice(['direct', 'inverse']);
    let texte, phrase, figureSvg;
    if (sens === 'direct') {
        texte = '<p><b>La figure est formée de quatre petits carrés identiques.</b> '
            + `Le côté de chaque petit carré mesure ${c} cm. Complète avec les bonnes étiquettes.</p>`;
        phrase = `Aire d'un petit carré : ${c} × ${c} = {{${petit}}} cm². `
            + `Aire de la figure entière : 4 × ${petit} = {{${figure}}} {{cm²}}`;
        figureSvg = svgQuatreCarres(c);
    } else {
        texte = '<p><b>La figure est formée de quatre petits carrés identiques. '
            + `Son aire totale est ${figure} cm².</b> Complète avec les bonnes étiquettes.</p>`;
        phrase = `Aire d'un petit carré : ${figure} ÷ 4 = {{${petit}}} cm². `
            + `Côté d'un petit carré : {{${c}}} {{cm}}`;
        figureSvg = svgQuatreCarres(null);
    }
    const distracteurs = filtrerDistracteurs(phrase, [
        String(2 * c),          // le double au lieu du carré
        String(4 * c),          // périmètre d'un petit carré
        String(2 * petit),      // 2 carrés au lieu de 4
        'cm', 'cm²', 'cm³',     // unités fausses (la bonne est filtrée d'office)
    ]).slice(0, 4);
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_AIRES), mode: 'drag', distracteurs, elements_additionnels: figureSvg });
    payload._meta.params = { c, petit, figure, sens };
    return payload;
};

const genAiresQcm = () => {
    const direction = randomChoice(['directe', 'inverse']);
    let texte, correct, candidats, elements, params;
    if (direction === 'directe') {
        const c = randInt(2, 12);
        texte = '<p><b>La figure est formée de quatre petits carrés identiques '
            + `de ${c} cm de côté.</b> Quelle est l'aire de la figure entière ?</p>`;
        correct = `${4 * c * c} cm²`;
        candidats = shuffle([
            `${c * c} cm²`,        // aire d'UN petit carré (oubli des 4)
            `${4 * c} cm`,         // confusion aire/longueur
            `${4 * c * c} cm³`,    // bonne valeur, mauvaise unité
            `${2 * c * c} cm²`,    // 2 carrés au lieu de 4
        ]);
        elements = svgQuatreCarres(c);
        params = { direction, c };
    } else {
        // c en demi-unités entières : c2 = 2c ∈ {3, 5, 7, 9, 11} (décimaux du cadrage) ∪ {4, 6, 8, 10, 12}
        const c2 = randInt(3, 12);
        const aire = c2 * c2;                       // aire totale = (2c)² = c2², entière
        const cote = frFrac(c2 * 5, 10);            // c = c2/2
        texte = '<p><b>La figure est formée de quatre petits carrés identiques. '
            + `Son aire totale est ${aire} cm².</b> Quelle est la longueur du côté d'un petit carré ?</p>`;
        correct = `${cote} cm`;
        candidats = shuffle([
            `${c2} cm`,                              // côté du GRAND carré (2c)
            `${frFrac(c2 * c2 * 25, 100)} cm`,       // c² : l'aire d'un petit carré prise pour une longueur
            `${frFrac(c2 * c2 * 50, 100)} cm`,       // la moitié de l'aire
            `${aire} cm`,                            // l'aire totale prise pour une longueur
            // (c2 = 4 : « 4 cm » sort deux fois — le Set dédoublonne, 3 restent)
        ]);
        elements = svgQuatreCarres(null);
        params = { direction, c2, aire };
    }
    const distincts = [...new Set(candidats)].filter(x => x !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte,
        { ...opts(L_AIRES), elements_additionnels: elements });
    payload._meta.params = params;
    return payload;
};

// ═════════ Carte 4 — Volume du cube (Ex.18) ═════════

const genVolumeTrous = () => {
    const contexte = randomChoice(['petitsCubes', 'arete']);
    let texte, phrase, params;
    if (contexte === 'petitsCubes') {
        const n = randInt(2, 5);
        const habillage = n === 3 && randomChoice([true, false])
            ? 'Le Rubik\'s Cube ci-contre est formé de petits cubes d\'arête 1 cm.'
            : 'Le grand cube ci-contre est construit avec des petits cubes d\'arête 1 cm.';
        texte = `<p><b>${habillage}</b> Complète. (Écris les nombres en chiffres.)</p>`;
        phrase = `Nombre de petits cubes sur chaque arête : {{${n}}}. `
            + `Volume du grand cube : {{${n}}} × {{${n}}} × {{${n}}} = {{${n ** 3}}} cm³`;
        params = { contexte, n };
        const payload = makeTexteTrou(phrase, texte,
            { ...opts(L_VOLUME), mode: 'saisie', elements_additionnels: svgCube(n, null) });
        payload._meta.params = params;
        return payload;
    }
    const a = randInt(2, 9);   // 10 exclu : le cube de 10 appartient à gen_928 (O30)
    const objet = randomChoice(['Un cube', 'Un dé', 'Une boîte cubique']);
    texte = `<p><b>${objet} a une arête de ${a} cm.</b> Complète le calcul de son volume. `
        + '(Écris les nombres en chiffres.)</p>';
    phrase = `Volume = arête × arête × arête = {{${a}}} × {{${a}}} × {{${a}}} = {{${a ** 3}}} cm³`;
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_VOLUME), mode: 'saisie', elements_additionnels: svgCube(1, a) });
    payload._meta.params = { contexte, a };
    return payload;
};

const genVolumeQcm = () => {
    const n = randInt(2, 5);
    const question = randomChoice([
        'Combien de petits cubes faut-il, en tout, pour construire ce grand cube ?',
        'Chaque petit cube a un volume de 1 cm³. Quel est le volume du grand cube, en cm³ ?',
    ]);
    const texte = '<p><b>Le grand cube ci-contre est construit avec des petits cubes '
        + `d'arête 1 cm.</b> ${question}</p>`;
    const correct = String(n ** 3);
    // familles dédupliquées numériquement (3n = n² pour n = 3 ; 6n² = 2n³ pour n = 3),
    // en rotation (mélange avant coupe)
    const candidats = shuffle([n * n, 3 * n, 6 * n * n, n * n + n, 4 * n * n])
        .filter(v => v !== n ** 3)
        .map(String);
    const distincts = [...new Set(candidats)].slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte,
        { ...opts(L_VOLUME), elements_additionnels: svgCube(n, null) });
    payload._meta.params = { n, question };
    return payload;
};

// ═════════ Carte 5 — Carrés et cubes de décimaux (Créé n°17) ═════════

// dixièmes admissibles pour un carré : 0,1 à 2,5 sauf les entiers (10, 20)
const T_CARRES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 23, 24, 25];

const genDecimauxTrous = () => {
    const [t1, t2] = shuffle(T_CARRES).slice(0, 2);
    const u = randInt(1, 5);
    // Une égalité par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // égalités à trous, l'impression coupait la ligne au milieu).
    const phrase = `${frT(t1)}² = {{${frFrac(t1 * t1, 100)}}}\n`
        + `${frT(t2)}² = {{${frFrac(t2 * t2, 100)}}}\n`
        + `${frT(u)}³ = {{${frFrac(u * u * u, 1000)}}}`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Calcule.</b> Écris chaque résultat en écriture décimale (ex. : 0,25).</p>',
        { ...opts(L_DECIMAUX), mode: 'saisie' });
    payload._meta.params = { t1, t2, u };
    return payload;
};

const genDecimauxQcm = () => {
    const type = randomChoice(['carre', 'cube']);
    let texte, correct, candidats, params;
    if (type === 'carre') {
        const t = randomChoice(T_CARRES);
        texte = `<p><b>Calcule : ${frT(t)}² = ?</b></p>`;
        correct = frFrac(t * t, 100);
        candidats = [
            frFrac(t * t * 10, 100),    // virgule décalée (0,5² → 2,5)
            frT(2 * t),                 // d × 2 (1,2² → 2,4)
            frFrac(t * t, 1000),        // virgule décalée dans l'autre sens
            frFrac(2 * t * t, 100),     // le double du carré
        ];
        params = { type, t };
    } else {
        const u = randInt(1, 5);
        texte = `<p><b>Calcule : ${frT(u)}³ = ?</b></p>`;
        correct = frFrac(u * u * u, 1000);
        candidats = [
            frFrac(u * u * u, 100),     // virgule décalée (0,1³ → 0,01)
            frT(3 * u),                 // d × 3 (0,1³ → 0,3)
            frFrac(u * u, 100),         // carré au lieu du cube
            frFrac(2 * u * u * u, 1000), // le double du cube
        ];
        params = { type, u };
    }
    const distincts = [...new Set(candidats)].filter(x => x !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte, opts(L_DECIMAUX));
    payload._meta.params = params;
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    boncalcul_qcm_unique: genBonCalculQcm,
    boncalcul_relier: genBonCalculRelier,
    vf_tableau_vf: genVfNotation,
    aires_texte_trou_drag: genAiresDrag,
    aires_qcm_unique: genAiresQcm,
    volume_texte_trou_saisie: genVolumeTrous,
    volume_qcm_unique: genVolumeQcm,
    decimaux_texte_trou_saisie: genDecimauxTrous,
    decimaux_qcm_unique: genDecimauxQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier', tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'boncalcul', label: L_BONCALCUL, formatsList: buildFormats('boncalcul') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'aires', label: L_AIRES, formatsList: buildFormats('aires') },
    { id: 'volume', label: L_VOLUME, formatsList: buildFormats('volume') },
    { id: 'decimaux', label: L_DECIMAUX, formatsList: buildFormats('decimaux') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
